import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@ascend/shared/components';
import {
  DataTable,
  FilterTabs,
  PaginationControls,
  SearchBar,
  TABLE_PAGE_SIZE_OPTIONS,
} from '../components/TableControls';
import { ADMIN_LENDING_POOLS, ADMIN_POOL_STATS } from '../data/mockAdapter';

// ─── Types ───────────────────────────────────────────────────────────────────

type PoolRow = (typeof ADMIN_LENDING_POOLS)[number];
type PoolSortKey = 'name' | 'facilityName' | 'tvlUsd' | 'utilizationPct' | 'apyPct' | 'lenderCount';
type UtilizationFilter = 'all' | 'high' | 'medium' | 'low';

const UTILIZATION_FILTERS: { value: UtilizationFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'high', label: 'High (>80%)' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low (<50%)' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

function statusBadge(status: PoolRow['status']) {
  const cfg = {
    critical: 'bg-red-100 text-red-700',
    high: 'bg-yellow-100 text-yellow-700',
    normal: 'bg-green-100 text-green-700',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${cfg[status]}`}>
      {status}
    </span>
  );
}

// ─── StatCard ────────────────────────────────────────────────────────────────

function StatCard({ title, value, subtitle, icon }: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">{title}</span>
        {icon}
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-bold">{value}</div>
      {subtitle && <div className="mt-1 text-xs sm:text-sm text-muted-foreground">{subtitle}</div>}
    </div>
  );
}

// ─── Page Component ──────────────────────────────────────────────────────────

export function StablecoinPoolsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<UtilizationFilter>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[0]);
  const [sortKey, setSortKey] = useState<PoolSortKey>('tvlUsd');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  function toggleSort(key: PoolSortKey) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  }

  const filtered = useMemo(() => {
    let result = [...ADMIN_LENDING_POOLS];

    // Utilization filter
    if (filter === 'high') result = result.filter((p) => p.utilizationPct > 80);
    else if (filter === 'medium') result = result.filter((p) => p.utilizationPct >= 50 && p.utilizationPct <= 80);
    else if (filter === 'low') result = result.filter((p) => p.utilizationPct < 50);

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (p) => p.name.toLowerCase().includes(q) || p.facilityName.toLowerCase().includes(q),
      );
    }

    // Sort
    result.sort((a, b) => {
      const cmp =
        typeof a[sortKey] === 'number'
          ? (a[sortKey] as number) - (b[sortKey] as number)
          : String(a[sortKey]).localeCompare(String(b[sortKey]));
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [filter, search, sortDir, sortKey]);

  const paginated = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(page, totalPages);
    return filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  }, [filtered, page, pageSize]);

  const filterCounts = useMemo(() => ({
    all: ADMIN_LENDING_POOLS.length,
    high: ADMIN_LENDING_POOLS.filter((p) => p.utilizationPct > 80).length,
    medium: ADMIN_LENDING_POOLS.filter((p) => p.utilizationPct >= 50 && p.utilizationPct <= 80).length,
    low: ADMIN_LENDING_POOLS.filter((p) => p.utilizationPct < 50).length,
  }), []);

  const columns = useMemo(
    () => [
      {
        id: 'name',
        label: 'Pool Name',
        sortable: true,
        sortKey: 'name' as const,
        renderCell: (p: PoolRow) => <span className="font-medium">{p.name}</span>,
      },
      {
        id: 'facilityName',
        label: 'Linked CF',
        sortable: true,
        sortKey: 'facilityName' as const,
        renderCell: (p: PoolRow) => <span className="text-sm text-muted-foreground">{p.facilityName}</span>,
      },
      {
        id: 'tvlUsd',
        label: 'TVL',
        align: 'right' as const,
        sortable: true,
        sortKey: 'tvlUsd' as const,
        renderCell: (p: PoolRow) => fmtCurrency(p.tvlUsd),
      },
      {
        id: 'utilizationPct',
        label: 'Utilization',
        align: 'right' as const,
        sortable: true,
        sortKey: 'utilizationPct' as const,
        renderCell: (p: PoolRow) => `${p.utilizationPct.toFixed(1)}%`,
      },
      {
        id: 'apyPct',
        label: 'APY',
        align: 'right' as const,
        sortable: true,
        sortKey: 'apyPct' as const,
        renderCell: (p: PoolRow) => `${p.apyPct.toFixed(1)}%`,
      },
      {
        id: 'lenderCount',
        label: 'Lenders',
        align: 'right' as const,
        sortable: true,
        sortKey: 'lenderCount' as const,
        renderCell: (p: PoolRow) => p.lenderCount,
      },
      {
        id: 'status',
        label: 'Status',
        renderCell: (p: PoolRow) => statusBadge(p.status),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex items-start gap-3">
        <Icon name="streamline:bank" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Stablecoin Pools</h1>
          <p className="text-sm text-muted-foreground">
            Monitor stablecoin liquidity pools across all credit facilities.
          </p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard
          title="Total TVL"
          value={fmtCurrency(ADMIN_POOL_STATS.totalTvl)}
          subtitle="Across all pools"
          icon={<Icon name="streamline:money-cash-coins-stack-accounting-billing-payment-stack-cash-coins-currency-money-finance" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Average APY"
          value={`${ADMIN_POOL_STATS.avgApy.toFixed(1)}%`}
          subtitle="Weighted by pool"
          icon={<Icon name="streamline:discount-percent-circle" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Avg Utilization"
          value={`${ADMIN_POOL_STATS.avgUtilization.toFixed(1)}%`}
          subtitle="Across all pools"
          icon={<Icon name="streamline:money-graph-bar-product-data-bars-analysis-analytics-graph-business-chart" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Active Pools"
          value={String(ADMIN_POOL_STATS.activePoolCount)}
          subtitle="Linked to active CFs"
          icon={<Icon name="streamline:bank" className="h-5 w-5 text-muted-foreground" />}
        />
      </div>

      {/* Filters & search */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <FilterTabs<UtilizationFilter>
          filters={UTILIZATION_FILTERS}
          active={filter}
          onChange={(value) => {
            setFilter(value);
            setPage(1);
          }}
          counts={filterCounts}
        />
        <SearchBar
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Search by pool or facility name"
          className="w-full sm:w-80"
        />
      </div>

      {/* Table */}
      <DataTable<PoolRow, PoolSortKey>
        columns={columns}
        rows={paginated}
        rowKey={(row) => row.id}
        sort={{
          key: sortKey,
          direction: sortDir,
          onSort: toggleSort,
        }}
        onRowClick={(row) => navigate(`/stablecoin-pools/${row.id}`)}
        rowClassName={() => 'cursor-pointer'}
        emptyMessage="No pools match your filters."
      />

      {/* Pagination */}
      <PaginationControls
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalItems={filtered.length}
        label="pools"
        pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
      />
    </div>
  );
}
